import React, { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import { useParams } from 'react-router-dom';
import {
  Box,
  Chip,
  IconButton,
  Stack,
  SxProps,
  Tooltip,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import FlagIcon from '@mui/icons-material/Flag';

import { IQuestion } from '../../../../store/QuestionSlice';
import {
  ISubmission,
  ISubmissionQuestion,
} from '../../../../store/SubmissionSlice';
import {
  getBannerStatusesFromLocalStorage,
  getQuestionDifficultyChipColor,
  setBannerStatusToLocalStorage,
} from '../../../../helper';
import { reportResources } from '../../../../store/ResourcesSlice';
import { getAuthState } from '../../../../store/AuthSlice';
import { useAppSelector } from '../../../../hooks';
import { TP_COLORS } from '../../../../constants';
import ReportModal from '../../../../components/Modals/ReportModal';
import QuestionTimer from './Timer/QuestionTimer';
import AiHintPreferenceBanner from './AiHintPreferenceBanner';

type TQuestionDetailViewerProps = {
  questionData: IQuestion;
  selectedQuestion: ISubmissionQuestion;
  submissionData: ISubmission;
};

const containerStyles: SxProps = {
  backgroundColor: '#F1FBFA',
  borderRadius: '20px',
  padding: '30px 15px',
  minHeight: '300px',
};

const labelStyles: React.CSSProperties = {
  backgroundColor: '#E2E8F0',
  color: '#1E293B',
  fontWeight: 600,
  borderRadius: '5px',
};

const QuestionDetailViewer: React.FC<TQuestionDetailViewerProps> = ({
  questionData,
  selectedQuestion,
  submissionData,
}) => {
  const params = useParams();
  const dispatch = useDispatch();
  const theme = useTheme();
  const upLg = useMediaQuery(theme.breakpoints.up('lg'));

  // Store
  const authState = useAppSelector(getAuthState);

  const authUser = authState.user;

  const questionOrder = (selectedQuestion.order + 1)
    .toString()
    .padStart(2, '0');

  const questionMarks = questionData.marks
    ? questionData.marks.toString().padStart(2, '0')
    : '';

  let questionDetail = questionData.details || '';

  if (
    selectedQuestion.isOpenEnded &&
    questionData.openEnded &&
    questionData.openEnded.question
  ) {
    const openEndedQuestion = questionData.openEnded.question;

    questionDetail = openEndedQuestion.startsWith('.')
      ? openEndedQuestion.trim().substring(1)
      : openEndedQuestion.trim();
  }

  // State
  const [state, setState] = useState({
    isReportQuestionModalOpen: false,
  });
  const [isBannerVisible, setShowBanner] = useState(false);

  useEffect(() => {
    const bannerStatuses = getBannerStatusesFromLocalStorage();
    if (
      questionData.difficulty === 'HARD' &&
      !bannerStatuses.AiHintBannerShown
    ) {
      setShowBanner(true);
    }
  }, [questionData.difficulty]);

  const toggleReportQuestionModal = () => {
    setState((prevState) => ({
      ...prevState,
      isReportQuestionModalOpen: !prevState.isReportQuestionModalOpen,
    }));
  };

  const reportQuestion = (reason: string) => {
    if (params.subtopicId && authUser && authUser._id) {
      dispatch(
        reportResources({
          questionId: questionData._id,
          subTopicId: params.subtopicId,
          userId: authUser._id,
          reason,
        })
      );
    }

    toggleReportQuestionModal();
  };

  const handleBannerClose = () => {
    setShowBanner(false);
    setBannerStatusToLocalStorage('AiHintBannerShown', true);
  };

  return (
    <>
      <Box
        sx={{
          ...containerStyles,
          ...(upLg
            ? {
                position: 'sticky',
                top: '100px',
              }
            : {}),
        }}
      >
        <Stack
          gap="10px"
          direction="row"
          alignItems="center"
          flexWrap="wrap"
          style={{ marginBottom: '15px' }}
        >
          <Chip label={`Question ${questionOrder}`} style={labelStyles} />
          <Chip
            label={`${questionMarks} Mark(s)`}
            style={{
              ...labelStyles,
              backgroundColor: '#334155',
              color: TP_COLORS.white,
            }}
          />
          <Chip
            label={questionData.difficulty}
            color={getQuestionDifficultyChipColor(questionData.difficulty)}
            style={{
              fontWeight: labelStyles.fontWeight,
              borderRadius: labelStyles.borderRadius,
            }}
          />
          {selectedQuestion.status === 'skipped' ? (
            <Chip label="Skipped" style={labelStyles} />
          ) : null}
          <Tooltip title="Report this question" placement="top">
            <IconButton
              size="small"
              onClick={toggleReportQuestionModal}
              style={{
                backgroundColor: '#E2E8F0',
                borderRadius: '4px',
              }}
            >
              <FlagIcon style={{ color: TP_COLORS.black }} />
            </IconButton>
          </Tooltip>
          {selectedQuestion &&
          selectedQuestion.status !== 'skipped' &&
          selectedQuestion.timerStart &&
          selectedQuestion.timerEnd &&
          submissionData.status === 'completed' &&
          submissionData.isTimerEnabled &&
          questionData &&
          questionData._id === selectedQuestion.question ? (
            <QuestionTimer selectedQuestion={selectedQuestion} />
          ) : null}
        </Stack>
        <div>
          <div
            className="view ql-editor ql-viewer"
            style={{ marginBottom: '20px' }}
            // eslint-disable-next-line react/no-danger
            dangerouslySetInnerHTML={{
              __html: questionDetail,
            }}
          />
        </div>
      </Box>
      {state.isReportQuestionModalOpen && (
        <ReportModal
          open={state.isReportQuestionModalOpen}
          type="question"
          onClose={toggleReportQuestionModal}
          onConfirm={reportQuestion}
        />
      )}
      {isBannerVisible && (
        <AiHintPreferenceBanner
          open={isBannerVisible}
          onClose={handleBannerClose}
        />
      )}
    </>
  );
};

export default QuestionDetailViewer;
